'use client';

import { c as _c } from "react/compiler-runtime";
import { isAutoTagged } from '@shared/all/domains/autoTag';
import { type Factiii } from '@shared/all/domains/factiiis';
import toast from '@shared/utils/helpers/toast';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../utils/trpc';
type Props = {
  factiii: Factiii;
  // The tags to judge: every entry of the factiii on the post, or one of them.
  tags: Parameters<typeof isAutoTagged>[1];
  postId: string;
  // Only the post's author is offered the removal here. Space staff and
  // admins may remove one too (factiiis.removeAutoTag), through the API.
  isAuthor: boolean;
  onRemoved?: () => void;
};

// Marks a mood or topic that Factiii's Auto Tag tool placed on a post
// (.specs/auto-tag.md) and lets the author take it off. The ordinary Delete
// only removes the viewer's own tag, and this one is factiii-bot's. Renders
// nothing for any other tag, so callers can mount it unconditionally.
export default function AutoTagNotice(t0) {
  const $ = _c(13);
  const {
    factiii,
    tags,
    postId,
    isAuthor,
    onRemoved
  } = t0;
  const [isRemoving, setIsRemoving] = useState(false);
  const {
    refreshPost
  } = usePostsShared(client);
  if (!isAutoTagged(factiii, tags)) {
    return null;
  }
  let t1;
  if ($[0] !== factiii || $[1] !== isRemoving || $[2] !== onRemoved || $[3] !== postId || $[4] !== refreshPost) {
    t1 = async () => {
      if (isRemoving) {
        return;
      }
      setIsRemoving(true);
      const [error] = await client.factiiis.removeAutoTag.mutate({
        postId,
        factiiiId: factiii.id
      });
      setIsRemoving(false);
      if (error) {
        toast(error.message, "", "error");
        return;
      }
      await refreshPost(postId);
      onRemoved?.();
    };
    $[0] = factiii;
    $[1] = isRemoving;
    $[2] = onRemoved;
    $[3] = postId;
    $[4] = refreshPost;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const remove = t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="text-xs text-textSecondary">Auto-tagged</span>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== isAuthor || $[8] !== isRemoving || $[9] !== remove) {
    t3 = isAuthor && <button type="button" onClick={e => {
      e.stopPropagation();
      remove();
    }} disabled={isRemoving} className="flex items-center gap-1 rounded-lg px-2 py-1 text-destructive disabled:opacity-50"><Trash2 size={14} /><span className="text-xs">{isRemoving ? "Removing..." : "Remove"}</span></button>;
    $[7] = isAuthor;
    $[8] = isRemoving;
    $[9] = remove;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <div className="mt-3 flex items-center justify-between gap-2 border-t border-ds-border-subtle pt-2">{t2}{t3}</div>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}